import React, { useEffect, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { IconSparkle, IconDevice, IconServer, IconClose } from './hudIcons.jsx';

// «رندرِ واقعی» — قدمِ ۱: خروجی گرفتن از صحنه‌ی سه‌بعدی (فایلِ استانداردِ GLB) و آپلود روی سرور، بعد پیگیریِ
// وضعیتِ رندر (در صف / در حالِ رندر / آماده). سرور (قدمِ ۲) با Blender نور را «می‌پزد» و فایلِ واقعی‌شده را
// برمی‌گرداند (قدمِ ۳).
//
// قراردادِ API (هم‌دامنه با خودِ سایت، چون همان سرورِ Express فایل‌های سایت را هم می‌دهد):
//   POST /api/render-jobs            بدنه = فایلِ GLB (Content-Type: model/gltf-binary) → { id }
//   GET  /api/render-jobs/:id        → { id, status: queued|rendering|done|failed, progress (0..1), error?, resultUrl? }

const API_BASE = '/api/render-jobs';
const POLL_MS = 4000;
const MAX_TEXTURE_SIZE = 1024;

const TEXTURE_KEYS = ['map', 'normalMap', 'roughnessMap', 'aoMap', 'metalnessMap', 'emissiveMap'];

function isJpegSource(tex) {
  const src = tex?.image?.src || tex?.source?.data?.src || '';
  return /\.jpe?g(\?|$)/i.test(src);
}

// حجمِ فایل: در صحنه هر سطح Textureِ مخصوصِ خودش را دارد (با تکرار/جابه‌جاییِ متفاوت) ولی عکسِ مشترک. خروجیِ
// glTF برای هر Texture (و مخصوصاً برای ترکیبِ roughness/metalness) یک عکسِ PNGِ جدا می‌ساخت — صدها عکسِ
// تکراری و فایلی بزرگ‌تر از ۱۰۰ مگابایت (خطای ۴۱۳). راه‌حل: تکرار/جابه‌جاییِ هر سطح مستقیماً در UVِ هندسه‌ی
// خودش «پخته» می‌شود و همه‌ی سطح‌ها به یک Textureِ واحد برای هر عکس اشاره می‌کنند → هر عکس فقط یک بار
function canonicalizeTextures(root) {
  const canonical = new Map(); // source.uuid|colorSpace → Texture
  const getCanonical = (tex) => {
    const key = `${tex.source.uuid}|${tex.colorSpace}`;
    let c = canonical.get(key);
    if (!c) {
      c = new THREE.Texture();
      c.source = tex.source;
      c.colorSpace = tex.colorSpace;
      c.wrapS = THREE.RepeatWrapping;
      c.wrapT = THREE.RepeatWrapping;
      c.flipY = tex.flipY;
      if (isJpegSource(tex)) c.userData.mimeType = 'image/jpeg';
      c.anisotropy = tex.anisotropy;
      // بدونِ این، کارت گرافیک این نسخه را هیچ‌وقت بارگذاری نمی‌کند و موقعِ نمایش روی همین دستگاه (پختنِ نورِ
      // محلی) سیاه دیده می‌شد. برای خروجیِ فایل فرقی نمی‌کند
      c.needsUpdate = true;
      canonical.set(key, c);
    }
    return c;
  };
  const v = new THREE.Vector3();
  root.traverse((obj) => {
    if (!obj.isMesh || !obj.material || Array.isArray(obj.material)) return;
    const mat = obj.material;
    const ref = TEXTURE_KEYS.map((k) => mat[k]).find((t) => t && t.source);
    const geo = obj.geometry.clone();
    geo.deleteAttribute('uv2'); // UVِ دوم را سرور برای نقشه‌ی نور می‌سازد
    if (ref && geo.attributes.uv) {
      if (ref.matrixAutoUpdate) ref.updateMatrix();
      const m = ref.matrix;
      const uv = geo.attributes.uv;
      for (let i = 0; i < uv.count; i += 1) {
        v.set(uv.getX(i), uv.getY(i), 1).applyMatrix3(m);
        uv.setXY(i, v.x, v.y);
      }
      uv.needsUpdate = true;
    }
    obj.geometry = geo;
    const m2 = mat.clone();
    TEXTURE_KEYS.forEach((k) => {
      if (m2[k] && m2[k].source) m2[k] = getCanonical(m2[k]);
    });
    obj.material = m2;
  });
}

// ترکیبِ roughness+metalness را خودِ exporter برای هر جنس از نو می‌ساخت (بدونِ کش) — این افزونه برای هر جفت
// عکسِ مشترک فقط یک بار می‌سازد و آن را هم JPEG ذخیره می‌کند
function metalRoughCachePlugin(writer) {
  const original = writer.buildMetalRoughTextureAsync.bind(writer);
  const cache = new Map();
  writer.buildMetalRoughTextureAsync = (metalnessMap, roughnessMap) => {
    const key = `${metalnessMap?.source?.uuid || '-'}|${roughnessMap?.source?.uuid || '-'}`;
    if (!cache.has(key)) {
      cache.set(
        key,
        original(metalnessMap, roughnessMap).then((t) => {
          t.userData = { ...t.userData, mimeType: 'image/jpeg' };
          return t;
        })
      );
    }
    return cache.get(key);
  };
  return {};
}

// چه چیزهایی در فایلِ ارسالی نباشند: سایه‌های تقلبیِ کنج (AO — رندرِ واقعی خودش سایه‌ی واقعی می‌سازد)، نوارهای
// آبیِ انتخاب، هدف‌های نامرئیِ لمس، آدمک، خطوطِ عینکِ مهندسی و سایه‌ی تماسی. هر شیئی که userData.khNoExport
// داشته باشد (یا مادّه‌اش کاملاً شفاف باشد) حذف می‌شود. userData.khExportAlways: حتی اگر الان مخفی است (مثلاً
// سقف در حالتِ پیش‌فرض) صادر شود
function prepareExportClone(source) {
  const clone = source.clone(true);
  // لایه‌ها: در حالتِ نمایشِ رندر، ساختمانِ زنده روی لایه‌ی مخفی می‌رود (BakedLiveMask) — کپی همیشه معمولی باشد
  clone.traverse((o) => o.layers.set(0));
  clone.visible = true; // در حالتِ نمایشِ رندرِ واقعی، ساختمانِ قابلِ ویرایش مخفی است — خروجی باید همچنان کامل باشد
  const remove = [];
  clone.traverse((obj) => {
    if (obj.userData?.khExportAlways) obj.visible = true;
    if (obj === clone) return;
    const mat = obj.material;
    const invisibleMaterial = mat && !Array.isArray(mat) && mat.transparent && mat.opacity === 0;
    const isAoStrip = Boolean(mat && !Array.isArray(mat) && mat.alphaMap && mat.color?.getHexString?.() === '15110c');
    const isOverlay = Boolean(mat && !Array.isArray(mat) && mat.depthTest === false); // خطوطِ عینکِ مهندسی
    const noExport = obj.userData?.khNoExport || mat?.userData?.khNoExport || isAoStrip || isOverlay;
    if (noExport || invisibleMaterial) remove.push(obj);
  });
  remove.forEach((obj) => obj.parent && obj.parent.remove(obj));
  canonicalizeTextures(clone);
  // واحد: گروهِ اصلی مقیاسِ ۰٫۰۱ دارد (سانتی‌متر → متر)، پس خروجی به متر است
  clone.userData = { ...clone.userData, khoonero: { version: 1, units: 'meters' } };
  return clone;
}

// داخلِ <Canvas>: تابعِ exportGlb را روی exportRef می‌گذارد تا دکمه (بیرون از Canvas) صدایش بزند
// cloneRef (اختیاری): کپیِ آماده‌ی ساختمان (بدونِ ساختنِ فایل) — برای پختنِ نور روی همین دستگاه
export function SceneExporter({ exportRef, targetRef, cloneRef }) {
  const { scene } = useThree();
  useEffect(() => {
    if (cloneRef) cloneRef.current = () => prepareExportClone(targetRef?.current || scene);
    exportRef.current = () =>
      new Promise((resolve, reject) => {
        const source = targetRef?.current || scene;
        const clone = prepareExportClone(source);
        const exporter = new GLTFExporter();
        exporter.register(metalRoughCachePlugin);
        exporter.parse(
          clone,
          (result) => resolve(result),
          (error) => reject(error),
          { binary: true, onlyVisible: true, maxTextureSize: MAX_TEXTURE_SIZE }
        );
      });
    return () => {
      exportRef.current = null;
      if (cloneRef) cloneRef.current = null;
    };
  }, [exportRef, targetRef, cloneRef, scene]);
  return null;
}

function uploadGlb(buffer, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', API_BASE);
    xhr.setRequestHeader('Content-Type', 'model/gltf-binary');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          reject(new Error('bad-response'));
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('network'));
    xhr.send(new Blob([buffer], { type: 'model/gltf-binary' }));
  });
}

// آخرین کارِ رندر در مرورگر یادداشت می‌شود — رندر طول می‌کشد و کاربر ممکن است صفحه را ببندد؛ با برگشتن،
// وضعیت/نتیجه دوباره پیدا می‌شود
const LAST_JOB_KEY = 'kh-render-last-job';
function rememberJob(id) {
  try {
    if (id) localStorage.setItem(LAST_JOB_KEY, id);
    else localStorage.removeItem(LAST_JOB_KEY);
  } catch (e) {
    /* حالتِ خصوصی و ... */
  }
}
function recallJob() {
  try {
    return localStorage.getItem(LAST_JOB_KEY);
  } catch (e) {
    return null;
  }
}

const faPct = (v) => `${Math.round(Math.max(0, Math.min(1, v)) * 100).toLocaleString('fa-IR')}٪`;
const faMB = (bytes) => (bytes / (1024 * 1024)).toLocaleString('fa-IR', { maximumFractionDigits: 1 });

// دکمه + پنلِ «رندرِ واقعی» (بیرون از Canvas). exportRef: از SceneExporter.
// bakedActive / bakedStatus / onShowBaked(result) / onHideBaked: نمایشِ نتیجه در صحنه (قدمِ ۳ — BakedModel)
export function RenderUploadButton({ exportRef, onBeforeExport, bakedActive, bakedStatus, onShowBaked, onHideBaked, onLocalBake }) {
  const [open, setOpen] = useState(false);
  // phase: idle | exporting | uploading | queued | rendering | done | failed
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [sizeBytes, setSizeBytes] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { resultUrl, lightmapUrl, meta }
  const pollRef = useRef(null);
  const onShowRef = useRef(onShowBaked);
  onShowRef.current = onShowBaked;

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = (id, { delay = POLL_MS, autoShow = true } = {}) => {
    clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/${encodeURIComponent(id)}`);
        if (res.status === 404 || res.status === 400) {
          // کار روی سرور پاک شده (قدیمی‌تر از چند روز)
          rememberJob(null);
          setJobId(null);
          setPhase('idle');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const job = await res.json();
        setProgress(Number(job.progress) || 0);
        if (job.status === 'done') {
          setPhase('done');
          const r = job.resultUrl && job.lightmapUrl ? { id, resultUrl: job.resultUrl, lightmapUrl: job.lightmapUrl, meta: job.meta || null } : null;
          setResult(r);
          if (r && autoShow && onShowRef.current) onShowRef.current(r); // تازه آماده شد → مستقیم نشان بده
          return;
        }
        if (job.status === 'failed') {
          setPhase('failed');
          setError(job.error || 'رندر روی سرور ناموفق بود');
          return;
        }
        setPhase(job.status === 'rendering' ? 'rendering' : 'queued');
        poll(id);
      } catch (e) {
        poll(id, { autoShow }); // قطعیِ لحظه‌ای — دوباره
      }
    }, delay);
  };

  // بازگشت به صفحه: آخرین کار را پیدا کن (اگر هنوز در حالِ رندر است پیگیری ادامه می‌یابد؛ اگر آماده است دکمه‌ی
  // «نمایش» ظاهر می‌شود، ولی خودکار باز نمی‌شود)
  useEffect(() => {
    const id = recallJob();
    if (id) {
      setJobId(id);
      poll(id, { delay: 0, autoShow: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (!exportRef.current) return;
    setError('');
    setResult(null);
    if (bakedActive && onHideBaked) onHideBaked();
    setPhase('exporting');
    setProgress(0);
    try {
      if (onBeforeExport) onBeforeExport();
      // یک فریم صبر تا انتخاب‌ها پاک و صحنه به‌روز شود
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const glb = await exportRef.current();
      setSizeBytes(glb.byteLength);
      setPhase('uploading');
      const job = await uploadGlb(glb, setProgress);
      setJobId(job.id);
      rememberJob(job.id);
      setPhase('queued');
      setProgress(0);
      poll(job.id);
    } catch (e) {
      setPhase('failed');
      setError(
        String(e?.message || '').startsWith('HTTP') || e?.message === 'network' || e?.message === 'bad-response'
          ? 'ارسال به سرور ناموفق بود — سرورِ رندر هنوز آماده نیست یا اینترنت قطع است'
          : 'ساختنِ فایلِ سه‌بعدی ناموفق بود'
      );
    }
  };

  const busy = phase === 'exporting' || phase === 'uploading';
  const statusText = {
    idle: 'طراحی برای رندر به سرور فرستاده می‌شود (کندتر؛ وقتی آماده شد همین‌جا خبر می‌دهیم).',
    exporting: 'در حالِ آماده‌سازیِ فایلِ سه‌بعدی…',
    uploading: `در حالِ ارسال (${faMB(sizeBytes)} مگابایت) — ${faPct(progress)}`,
    queued: 'ارسال شد؛ در صفِ رندر است…',
    rendering: `در حالِ رندرِ واقعی روی سرور — ${faPct(progress)}`,
    done: bakedActive
      ? bakedStatus === 'loading'
        ? 'در حالِ بارگذاریِ رندرِ واقعی…'
        : bakedStatus === 'error'
          ? 'بارگذاریِ رندر ناموفق بود — دوباره «نمایش» را بزنید'
          : 'الان رندرِ واقعی را می‌بینید. نور ثابت است و درها بسته‌اند؛ برای تغییر، به طراحی برگردید.'
      : 'رندرِ واقعی آماده است.',
    failed: error,
  }[phase];

  return (
    <>
      <button
        type="button"
        className={`create-3d__render-btn ${phase === 'queued' || phase === 'rendering' ? 'is-working' : ''} ${phase === 'done' ? 'is-done' : ''}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setOpen((v) => !v)}
        aria-label="رندرِ واقعی"
      >
        <IconSparkle size={18} />
        <span>رندر</span>
        {(phase === 'queued' || phase === 'rendering' || phase === 'done') && <i className="create-3d__render-dot" />}
      </button>

      {open && (
        <div className="create-3d__render-panel" onPointerDown={(e) => e.stopPropagation()}>
          <div className="create-3d__render-panel-head">
            <span>رندرِ واقعی</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="بستن">
              <IconClose size={16} />
            </button>
          </div>
          {onLocalBake && !busy && (
            <>
              <button
                type="button"
                className="create-3d__render-start is-local"
                onClick={() => {
                  setOpen(false);
                  onLocalBake();
                }}
              >
                <IconDevice size={18} />
                <span>رندر روی همین دستگاه</span>
              </button>
              <p className="create-3d__render-meta create-3d__render-local-hint">
                نور روی کارت گرافیکِ همین گوشی/کامپیوتر پخته می‌شود و کم‌کم واقعی‌تر می‌شود. در این مدت صفحه را باز نگه دارید.
              </p>
              <div className="create-3d__render-sep">
                <IconServer size={14} />
                <span>یا روی سرور</span>
              </div>
            </>
          )}
          <p className={`create-3d__render-status ${phase === 'failed' ? 'is-error' : ''}`}>{statusText}</p>
          {(phase === 'uploading' || phase === 'rendering') && (
            <div className="create-3d__render-bar">
              <div style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          )}
          {jobId && phase !== 'failed' && <p className="create-3d__render-meta">شماره‌ی کار: {jobId}</p>}
          {phase === 'done' && result && (
            <button
              type="button"
              className="create-3d__render-start is-primary"
              onClick={() => (bakedActive ? onHideBaked && onHideBaked() : onShowBaked && onShowBaked(result))}
            >
              {bakedActive ? 'بازگشت به طراحی' : 'نمایشِ رندرِ واقعی'}
            </button>
          )}
          {(phase === 'idle' || phase === 'failed' || phase === 'done') && (
            <button type="button" className="create-3d__render-start" disabled={busy} onClick={start}>
              {phase === 'idle' ? 'ارسال برای رندر' : 'ارسالِ دوباره'}
            </button>
          )}
        </div>
      )}
    </>
  );
}
